import {
  Button,
  Input,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@solinkcorp/solink-ui';
import {
  MAX_STATE_DESCRIPTION_LENGTH,
  MAX_STATE_NAME_LENGTH,
  MAX_STATES,
  MIN_STATES,
  type SpotCheckState,
  type UseSpotCheckForm,
} from 'components/SpotCheckPanel/hooks/useSpotCheckForm';
import { Plus as PlusIcon, Trash2 } from 'lucide-react';
import React from 'react';
import useLocale from 'src/hooks/useLocale';
import { getValidationErrorMessage } from 'utils/yupUtils';

interface StatesListProps {
  spotCheckForm: UseSpotCheckForm;
  hideSectionTitle?: boolean;
}

const STATE_NAME_USER_INPUT_TOLERANCE = 10;
const STATE_DESCRIPTION_USER_INPUT_TOLERANCE = 20;

export default function StatesList(props: StatesListProps) {
  const { spotCheckForm, hideSectionTitle = false } = props;
  const {
    aiChatAnswerStates,
    addAnswerState,
    updateAnswerState,
    removeAnswerState,
    spotCheckValidationError,
    livePreviewValidationError,
    eventLivePreviewValidationError,
  } = spotCheckForm;

  const locale = useLocale();

  // Get the appropriate validation error based on the mode
  const validationError =
    spotCheckValidationError || livePreviewValidationError || eventLivePreviewValidationError;

  const handleNameChange = (id: string, value: string) => {
    const truncatedValue = value.slice(0, MAX_STATE_NAME_LENGTH + STATE_NAME_USER_INPUT_TOLERANCE);
    updateAnswerState(id, { name: truncatedValue });
  };

  const handleDescriptionChange = (id: string, value: string) => {
    const truncatedValue = value.slice(
      0,
      MAX_STATE_DESCRIPTION_LENGTH + STATE_DESCRIPTION_USER_INPUT_TOLERANCE,
    );
    updateAnswerState(id, { description: truncatedValue });
  };

  const handleRemoveAnswerState = (id: string, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    removeAnswerState(id);
  };

  const canAddAnswerState = aiChatAnswerStates.length < MAX_STATES;
  const canRemoveAnswerState = aiChatAnswerStates.length > MIN_STATES;

  return (
    <div className="tw flex flex-col gap-3">
      {hideSectionTitle ? (
        <p className="text-foreground-secondary body-sm">
          {locale.scheduledAIChat.statesDescription}
        </p>
      ) : (
        <div>
          <p className="text-foreground-primary body-md-strong">
            {locale.scheduledAIChat.statesLabel}
          </p>
          <p className="text-foreground-secondary body-sm">
            {locale.scheduledAIChat.statesDescription}
          </p>
        </div>
      )}
      {aiChatAnswerStates.map((state: SpotCheckState, index: number) => {
        const nameError = getValidationErrorMessage(
          validationError,
          `aiChatAnswerStates[${index}].name`,
        );
        const descriptionError = getValidationErrorMessage(
          validationError,
          `aiChatAnswerStates[${index}].description`,
        );
        const responseLabel = `${locale.scheduledAIChat.stateNameLabel} *`;
        const descriptionLabel = `${locale.scheduledAIChat.stateDescriptionLabel} *`;

        const responseHeading = `${locale.scheduledAIChat.stateNameLabel} ${index + 1}`;

        return (
          <div
            key={state.id}
            className="tw rounded-md border border-border-muted bg-background-surface p-3"
          >
            <div className="tw mb-2 flex items-start justify-between gap-2">
              <span className="text-foreground-primary label-sm">{responseHeading}</span>
              {canRemoveAnswerState ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className="tw -mr-1 -mt-1 shrink-0 !text-foreground-red hover:bg-interaction-neutral-background-hover hover:!text-foreground-red [&_svg]:!text-foreground-red"
                  title={locale.general?.remove || 'Remove'}
                  onClick={(event) => handleRemoveAnswerState(state.id, event)}
                >
                  <Trash2 className="size-4 shrink-0" />
                </Button>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      className="tw -mr-1 -mt-1 inline-flex shrink-0 cursor-not-allowed rounded-md p-1.5 text-foreground-muted"
                      aria-label={locale.scheduledAIChat.mustHaveOneState}
                    >
                      <Trash2 className="size-4 shrink-0 opacity-50" aria-hidden />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="tw max-w-xs text-left">
                    {locale.scheduledAIChat.mustHaveOneState}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
            <div className="tw flex min-w-0 flex-col gap-1.5">
              <Input
                value={state.name}
                onChange={(event) => handleNameChange(state.id, event.target.value)}
                name={`state-name-${state.id}`}
                placeholder={responseLabel}
                aria-label={`${responseHeading} — ${responseLabel}`}
                aria-invalid={!!nameError}
                maxLength={MAX_STATE_NAME_LENGTH + STATE_NAME_USER_INPUT_TOLERANCE}
                className="tw body-md-strong"
              />
              <Textarea
                value={state.description}
                onChange={(event) => handleDescriptionChange(state.id, event.target.value)}
                name={`state-description-${state.id}`}
                rows={2}
                placeholder={descriptionLabel}
                aria-label={`${responseHeading} — ${descriptionLabel}`}
                aria-invalid={!!descriptionError}
                maxLength={MAX_STATE_DESCRIPTION_LENGTH + STATE_DESCRIPTION_USER_INPUT_TOLERANCE}
                className="tw min-h-[2.25rem] resize-none"
              />
              {nameError ? <p className="tw text-status-red caption">{nameError}</p> : null}
              {descriptionError ? (
                <p className="tw text-status-red caption">{descriptionError}</p>
              ) : null}
            </div>
          </div>
        );
      })}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="tw w-fit"
        disabled={!canAddAnswerState}
        onClick={addAnswerState}
      >
        <PlusIcon className="size-4" />
        {locale.scheduledAIChat.addResponse}
      </Button>
    </div>
  );
}
